/*
 * @Author: lijinyang
 * @FilePath: /mall-fe/src/comps/CategoriesDrawer.tsx
 * @Description: 分类专区组件
 *
 */
import { clone } from 'rambdax';
import { useState } from 'react';
import { cls } from 'reactutils';
import SearchMoreDrawer from './SearchMoreDrawer';

export type SysNo = string | number;
export type LabelKey = 'name' | 'label';
export type SubItem = {
  [label in LabelKey]?: string;
} & {
  sysNo: SysNo;
  children?: Array<SubItem>;
};

export type Value = {
  [key: string]: SysNo | Array<SysNo>;
};

export type Item = {
  type: 'checkbox' | 'radio';
  title: string;
  labelKey: LabelKey; // subItem中可能为name或者label两个字段
  prop: string; // 筛选条件字段名称
  show: boolean;
  hotSubList: Array<SubItem>;
  hasMoreBtn: boolean;
  totalSubList?: Array<SubItem>;
};

export type SearchConditionDrawerProps = {
  value: Value;
  conditionList: Array<Item>;
  onChange: (value: Value, prop: any) => void;
};

export default function CategoriesDrawer({
  conditionList = [],
  value,
  onChange,
}: SearchConditionDrawerProps) {
  const [visibleMoreDrawer, setVisibleMoreDrawer] = useState(false);
  const [activeMoreItem, setActiveMoreItem] = useState<Item>({
    type: 'checkbox',
    title: '',
    labelKey: 'name',
    prop: '',
    show: true,
    hotSubList: [],
    totalSubList: [],
    hasMoreBtn: false,
  });
  /**
   * @description: 判断子分类是否被选中
   * @param {Item，SubItem } option 分类、子分类
   * @return {boolean} 是被被选中
   */
  function isSubItemActive({ type, prop }: Item, { sysNo }: SubItem): boolean {
    if (!value) return false;
    let flag = false;
    // 分类是 多选， 具备可选属性时，包含子类编号，置为已选
    if (type === 'checkbox') {
      const checkboxVal = (value[prop] || []) as Array<SysNo>;
      flag = checkboxVal.includes(sysNo);
    } else {
      // 分类是 单选， 具备可选属性时，包含子类编号，置为已选
      const radioVal = value[prop];
      flag = radioVal === sysNo;
    }

    if (prop === 'orderStatusList') {
      if (sysNo === 'waitSettlement' && value.waitSettlement) {
        flag = true;
      }
    }

    return flag;
  }
  /**
   * @description: 判断子分类是否被选中
   * @param {Item，SubItem } option 分类、子分类
   * @return {boolean} 是被被选中
   */
  function isSubMoreActive({ type, prop, hotSubList }: Item): boolean {
    if (!value) return false;
    let flag = false;
    // 分类是 多选， 已选中的分类中包含热门分类时，置为已选
    if (type === 'checkbox') {
      const checkboxVal = (value[prop] || []) as Array<SysNo>;
      flag = !!checkboxVal.find(
        (selectItem) => !hotSubList?.find((hotItem) => hotItem.sysNo === selectItem),
      );
    } else {
      // 分类是 单选， 具备可选属性时，包含子类编号，置为已选
      const radioVal = value[prop];
      flag = !hotSubList?.find((hotItem) => hotItem.sysNo === radioVal);
    }
    return flag;
  }
  /**
   * @description: 分类被选中操作处理
   * @param {Item，SubItem } option 分类、子分类
   * @return {boolean} 是被被选中
   */
  function handleClickItem({ type, prop }: Item, { sysNo }: SubItem) {
    const draftValue: any = clone(value);
    const hasWaitSettlementOrderStatus = false;
    // 分类是 ，置为已选
    if (type === 'checkbox') {
      const checkboxVal = (draftValue[prop] || []) as Array<SysNo>;
      const index = checkboxVal.findIndex((selectItem) => selectItem === sysNo);
      if (index === -1) {
        checkboxVal.push(sysNo);
      } else {
        checkboxVal.splice(index, 1);
      }
      draftValue[prop] = checkboxVal;
    } else {
      const radioVal = draftValue[prop];
      if (radioVal === sysNo) {
        draftValue[prop] = '';
      } else {
        draftValue[prop] = sysNo;
      }
    }
    // 默认被选中分类处理，这里逻辑待进一步确认 TODO
    if (hasWaitSettlementOrderStatus) {
      if (sysNo === 'waitSettlement') {
        // 待结算
        if (draftValue.waitSettlement) {
          draftValue.waitSettlement = false;
          draftValue.orderStatusList = (draftValue.orderStatusList as any).filter(
            (v) => v !== 'waitSettlement',
          );
        } else {
          draftValue.waitSettlement = true;
          if (!draftValue.orderStatusList.includes('waitSettlement')) {
            draftValue.orderStatusList.push('waitSettlement');
          }
        }
      } else {
        // 待结算编辑中
        if (draftValue.waitSettlement) {
          if (!draftValue.orderStatusList.includes('waitSettlement')) {
            draftValue.orderStatusList.push('waitSettlement');
          }
        }
      }
    }

    onChange(draftValue, prop);
  }
  /**
   * @description: 展示更多侧边容器
   * @param {Item  } option 分类
   * @return {null}
   */
  function showMoreDrawer(item: Item) {
    setActiveMoreItem(item);
    setVisibleMoreDrawer(true);
  }
  /**
   * @description: 展示更多侧边容器
   * @param {Item, SysNo| SysNo[]  } option 分类、系统编号
   * @return {null}
   */
  function handleChangeMore({ prop }: Item, subItemValue: SysNo | Array<SysNo>) {
    const draftValue = clone(value);
    draftValue[prop] = subItemValue;
    onChange(draftValue, prop);
  }

  return (
    <div>
      <div className="conditionWrap">
        {conditionList?.map((item, index) => (
          <div className="itemWrap" key={index}>
            <div className="itemTitle">
              <span>{item.title}</span>
              {item.type === 'radio' && <span className="itemSubTitle">单选</span>}
            </div>
            <div className="subListWrap">
              {item.hotSubList?.map((subItem, subIndex) => (
                <div
                  className={cls('subItemWrap', isSubItemActive(item, subItem) && 'active')}
                  key={subIndex}
                  onClick={() => {
                    handleClickItem(item, subItem);
                  }}
                >
                  {subItem[item.labelKey]}
                </div>
              ))}
              {item.type === 'checkbox' && item.hasMoreBtn && item.hotSubList?.length >= 8 && (
                <div
                  onClick={() => {
                    showMoreDrawer(item);
                  }}
                  className={cls('subItemWrap', isSubMoreActive(item) && 'moreActive')}
                >
                  {`更多${item.title}`}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {/* 查询更多侧边窗口 */}
      <SearchMoreDrawer
        visible={visibleMoreDrawer}
        title={activeMoreItem.title}
        labelKey={activeMoreItem.labelKey}
        value={(value[activeMoreItem.prop] || []) as Array<SysNo>}
        list={activeMoreItem.totalSubList || []}
        onClose={() => {
          setVisibleMoreDrawer(false);
        }}
        onChange={(subItemValue) => {
          handleChangeMore(activeMoreItem, subItemValue);
        }}
      />
      <style jsx>{`
        .itemWrap {
          padding: 30px 20px 17px 20px;
          border-bottom: 2px solid #f9f9f9;
        }

        .itemWrap:last-child {
          border-bottom: none;
        }

        .itemTitle {
          font-size: 15px;
          color: #262626;
          line-height: 13px;
          font-weight: bold;
          margin-bottom: 17px;
        }

        .itemSubTitle {
          font-size: 12px;
          color: #999999;
          margin-left: 12px;
        }

        .subListWrap {
          margin-bottom: -14px;
        }

        .subItemWrap {
          width: 84px;
          height: 30px;
          line-height: 30px;
          display: inline-block;
          vertical-align: middle;
          text-align: center;
          background: #f5f5f5;
          margin-right: 14px;
          font-size: 14px;
          color: #262626;
          padding: 0 5px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          border-radius: 100px;
          box-sizing: border-box;
          margin-bottom: 14px;
        }

        .subItemWrap.active {
          background-color: #edf3ff;
          border-color: var(--primaryColor);
          color: var(--primaryColor);
        }

        .subItemWrap.moreActive {
          background-color: #edf3ff;
          border-color: var(--primaryColor);
          color: var(--primaryColor);
        }

        .subItemWrap:nth-child(3n) {
          margin-right: 0;
        }
      `}</style>
    </div>
  );
}
